// =====================================================================
// src/lib/importacao.js — o Importar do MdM, sem DOM
//   (index.html 2755–3407 `imp_*`, 3920–4074 anúncios, 4110–4492
//    `impRel_*`, 4503–4586 desfazer)
//
// Tudo que decide está aqui, copiado função por função: como o arquivo
// vira linhas (CSV `;`/`,`, Texto→Colunas quando veio "desconfigurado"),
// que coluna é o quê, o formato da data (coletivo, >12 decide, Airbnb
// desempata em MM/DD), o valor, a dedup inteligente (mesmo flat + check-in
// + nome → IGNORAR / ATUALIZAR_VALOR / CONFIRMAR), as "sumidas", o Booking
// (colunas fixas, data "2 de maio de 2026", líquido = pagamento − comissão)
// e o Relatório Completo por título do anúncio. O que fala com o banco é
// da tela (`modulos/importacao.js`); a leitura do .xlsx é do `XLSX` global
// (site/vendor), que a tela injeta — este arquivo não sabe o que é
// FileReader.
//
// Divergências de domínio (0051): o hóspede entra com o nome inteiro e
// sobrenome nulo (§5.2); "remover sumidas" cancela em vez de apagar; a
// reserva existente chega no shape do SGH (`unidade_id`, `situacao`,
// `hospedes[]`, valor por `valores_das_reservas`) e `existenteDe` a traduz
// para o shape do MdM que estas funções comparam.
// =====================================================================

export const PLATAFORMAS_IMPORTACAO = Object.freeze([
  { chave: 'airbnb', rotulo: 'Airbnb' }, { chave: 'booking', rotulo: 'Booking' }, { chave: 'direto', rotulo: 'Direto' }, { chave: 'outro', rotulo: 'Outro' },
]);
export const PLATAFORMAS_RELATORIO = Object.freeze([{ chave: 'airbnb', rotulo: 'Airbnb' }, { chave: 'booking', rotulo: 'Booking' }]);
export const OBS_ARQUIVO = 'Importado via arquivo';
export const OBS_RELATORIO = 'Importado via relatório completo';
export const OBS_NOVA = 'Importado via arquivo (confirmado como nova)';
export const NOME_SEM_HOSPEDE = 'Hóspede importado';

// `norm` (785): minúsculo, sem acento, aparado.
export const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
export const capitalizar = s => { const t = String(s || 'outro'); return t.charAt(0).toUpperCase() + t.slice(1); };

// ── Texto→Colunas (2760) ─────────────────────────────────────────────
export function splitVirgulaEspaco(linha) {
  const texto = String(linha == null ? '' : linha);
  const resultado = []; let atual = '', dentroAspas = false;
  for (const c of texto) {
    if (c === '"') dentroAspas = !dentroAspas;
    else if ((c === ',' || c === ' ' || c === '\t') && !dentroAspas) { if (atual.length > 0) { resultado.push(atual); atual = ''; } }
    else atual += c;
  }
  if (atual.length > 0) resultado.push(atual);
  return resultado;
}
export function estaDesconfigurada(rows) {
  if (!rows || rows.length < 2) return false;
  let unicoCampo = 0;
  rows.forEach(r => { if (!r) return; if (r.filter(c => c != null && String(c).trim() !== '').length <= 1) unicoCampo++; });
  return unicoCampo > rows.length * 0.5;
}
export function splitSoVirgula(linha) {
  const texto = String(linha == null ? '' : linha);
  const resultado = []; let atual = '', dentroAspas = false;
  for (const c of texto) {
    if (c === '"') dentroAspas = !dentroAspas;
    else if (c === ',' && !dentroAspas) { resultado.push(atual.trim()); atual = ''; }
    else atual += c;
  }
  resultado.push(atual.trim());
  return resultado;
}
// devolve { rows, reconfigurada }
export function reconfigurarRows(rows) {
  if (!estaDesconfigurada(rows)) return { rows, reconfigurada: false };
  return {
    reconfigurada: true,
    rows: rows.map((r, idx) => {
      if (!r) return r;
      const textoLinha = r.filter(c => c != null && String(c).trim() !== '').map(c => String(c).trim()).join(' ');
      return idx === 0 ? splitSoVirgula(textoLinha) : splitVirgulaEspaco(textoLinha);
    }),
  };
}
// o CSV (2844): separador `;` ou `,` pelo que há mais; aspas nas pontas caem
export function lerCsv(text) {
  const t = String(text || '');
  const sep = (t.split(';').length > t.split(',').length) ? ';' : ',';
  return t.split(/\r?\n/).filter(l => l.trim()).map(l => l.split(sep).map(c => c.replace(/^"|"$/g, '').trim()));
}
// o .xlsx (2853): a primeira planilha, linha a linha, datas como aaaa-mm-dd
export function lerPlanilha(XLSX, bytes) {
  const wb = XLSX.read(new Uint8Array(bytes), { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
}
export const ehCsv = nome => String(nome || '').toLowerCase().endsWith('.csv');

// ── colunas, datas, valores (2869–2965) ──────────────────────────────
const MAPA_COLUNAS = {
  nomeHospede: ['nome', 'guest', 'hospede', 'name', 'cliente', 'visitante', 'traveler', 'traveller'],
  checkIn:     ['checkin', 'check-in', 'check in', 'entrada', 'arrival', 'inicio', 'start', 'data entrada', 'from', 'data inicio', 'data de inicio', 'data inicial'],
  checkOut:    ['checkout', 'check-out', 'check out', 'saida', 'departure', 'fim', 'end date', 'termino', 'data saida', 'data fim', 'data de termino', 'data de saida', 'data de fim', 'data final', 'data termino'],
  valorTotal:  ['valor', 'total', 'price', 'amount', 'pago', 'paid', 'receita', 'diaria', 'rate', 'tarifa', 'earnings', 'ganhos', 'receita bruta'],
  telefone:    ['telefone', 'tel ', 'phone', 'celular', 'whatsapp', 'contato', 'contact'],
  email:       ['email', 'e-mail', 'mail'],
  status:      ['status', 'situacao', 'state'],
  noites:      ['noites', 'nights', '# of nights', 'num nights', 'qtd noites', 'qtde noites', 'dias', 'days', 'duration', 'duracao'],
};
export function detectarColunas(header) {
  const result = {};
  (header || []).forEach((col, idx) => {
    const colLower = norm(col);
    if (!colLower) return;
    for (const campo of Object.keys(MAPA_COLUNAS)) {
      if (result[campo] !== undefined) continue;
      if (MAPA_COLUNAS[campo].some(k => colLower.includes(norm(k)))) result[campo] = idx;
    }
  });
  return result;
}
export function detectarFormatoData(amostras, plataforma) {
  let p1MaiorQue12 = false, p2MaiorQue12 = false;
  (amostras || []).forEach(v => {
    const m = String(v == null ? '' : v).trim().match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (!m) return;
    if (parseInt(m[1], 10) > 12) p1MaiorQue12 = true;
    if (parseInt(m[2], 10) > 12) p2MaiorQue12 = true;
  });
  if (p1MaiorQue12 && !p2MaiorQue12) return 'DD/MM/YYYY';
  if (p2MaiorQue12 && !p1MaiorQue12) return 'MM/DD/YYYY';
  return plataforma === 'airbnb' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
}
export function parseData(val, formato) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substr(0, 10);
  const mIso = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
  if (mIso) return mIso[1] + '-' + mIso[2].padStart(2, '0') + '-' + mIso[3].padStart(2, '0');
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
  if (m) {
    const yr = m[3].length === 2 ? '20' + m[3] : m[3];
    const p1 = m[1].padStart(2, '0'), p2 = m[2].padStart(2, '0');
    if (formato === 'MM/DD/YYYY') {
      if (parseInt(p1, 10) > 12 || parseInt(p2, 10) > 31) return '';
      return yr + '-' + p1 + '-' + p2;
    }
    if (parseInt(p2, 10) > 12 || parseInt(p1, 10) > 31) return '';
    return yr + '-' + p2 + '-' + p1;
  }
  return '';   // nunca string crua para o Postgres (bug histórico "2 de maio ")
}
export function parseValor(val) {
  if (!val) return 0;
  let s = String(val).replace(/[^\d,\.]/g, '');
  if (s.indexOf(',') > s.indexOf('.') && s.indexOf(',') >= 0) s = s.replace(/\./g, '').replace(',', '.');
  return parseFloat(s) || 0;
}
export function somarDiasISO(isoData, dias) {
  if (!isoData || !/^\d{4}-\d{2}-\d{2}/.test(isoData)) return '';
  const n = parseInt(dias, 10);
  if (!isFinite(n) || n <= 0) return '';
  const d = new Date(isoData + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().substr(0, 10);
}
// Booking: "2 de maio de 2026" / "1 de abril, 2026" (4149)
const MESES_LONGOS = { janeiro: '01', fevereiro: '02', marco: '03', abril: '04', maio: '05', junho: '06', julho: '07', agosto: '08', setembro: '09', outubro: '10', novembro: '11', dezembro: '12' };
export function parseDataPtBrLonga(s) {
  if (!s) return '';
  const t = String(s).trim().toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ');
  const m = t.match(/^(\d{1,2})\s+de\s+([a-z\u00e7\u00e3\u00e2\u00e9\u00ed\u00f3\u00fa]+)\s+(?:de\s+)?(\d{4})$/i);
  if (!m) return '';
  const mes = MESES_LONGOS[m[2].normalize('NFD').replace(/[\u0300-\u036f]/g, '')];
  if (!mes) return '';
  return m[3] + '-' + mes + '-' + m[1].padStart(2, '0');
}
// o último número do nome da propriedade é o flat (4165)
export function extrairNumeroFlat(texto) {
  if (!texto) return '';
  const matches = String(texto).trim().match(/\d+/g);
  return matches && matches.length ? matches[matches.length - 1] : '';
}

// ── dedup (2967–3008) ────────────────────────────────────────────────
export const normalizarNome = n => String(n || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
export function ehDuplicata(novo, existente) {
  if (novo.flatId !== existente.flatId) return false;
  if (novo.checkIn !== existente.checkIn) return false;
  if (normalizarNome(novo.nomeHospede) !== normalizarNome(existente.nomeHospede)) return false;
  return Math.abs((parseFloat(novo.valorTotal) || 0) - (parseFloat(existente.valorTotal) || 0)) < 0.01;
}
export function ehMesma(novo, existente) {
  if (novo.checkIn !== existente.checkIn) return false;
  return normalizarNome(novo.nomeHospede) === normalizarNome(existente.nomeHospede);
}
// IGNORAR · ATUALIZAR_VALOR · CONFIRMAR · null (não é a mesma)
export function decidirAcao(novo, existente) {
  if (novo.flatId && existente.flatId && novo.flatId !== existente.flatId) return null;
  if (!ehMesma(novo, existente)) return null;
  const vN = parseFloat(novo.valorTotal) || 0, vE = parseFloat(existente.valorTotal) || 0;
  if (Math.abs(vN - vE) < 0.01) return 'IGNORAR';
  if (vN > 0 && vE === 0) return 'ATUALIZAR_VALOR';
  if (vN === 0 && vE > 0) return 'IGNORAR';
  return 'CONFIRMAR';
}
// A reserva do SGH no shape que o MdM compara. `valorDe` devolve null para
// quem não tem `financeiro` — aí toda existente vale 0, e um arquivo com
// valor vira ATUALIZAR_VALOR, que o banco recusa (0051). A tela trata isso
// como IGNORAR quando não edita financeiro (ver `planejar`).
export function existenteDe(r, { valorDe, hospedeDe }) {
  return {
    id: r.id, flatId: r.unidade_id, checkIn: r.check_in, checkOut: r.check_out, plataforma: r.plataforma || '',
    status: r.situacao, nomeHospede: hospedeDe ? hospedeDe(r) : '', valorTotal: valorDe ? (Number(valorDe(r)) || 0) : 0,
    observacoes: r.observacao || '', criadoEm: r.criado_em || '', comCheckin: (r.hospedes || []).some(h => h && h.chegada_em),
    // 0084: alguém dentro agora (check-in sem check-out) — não entra em "sumidas"
    comHospedeDentro: (r.hospedes || []).some(h => h && h.chegada_em && !h.saida_em),
  };
}

// ── Limpar duplicadas (3447–3518, 3890–3916) ─────────────────────────
// Agrupa as ATIVAS por flat + check-in + nome normalizado e classifica:
//   auto      — {keep, drop, motivo}: 1 com valor + N sem → apaga as sem
//               ("R$0 com par"); 2+ idênticas → mantém a mais antiga
//               ("idênticas")
//   ambi      — {reservas, motivo}: todas R$0 · valores diferentes · misto
//   zeroOrfas — importada com R$0 sem par, ainda não confirmada cortesia
// Divergência: "mais antiga" é por `criadoEm` (o MdM ordena pelo id, que
// lá é sequencial; aqui é uuid aleatório). E quem já fez check-in nunca é
// candidata a `drop` — a 0052 recusaria; a tela nem oferece.
const antigaPrimeiro = (a, b) => String(a.criadoEm || '').localeCompare(String(b.criadoEm || '')) || String(a.id).localeCompare(String(b.id));
export function detectarDuplicadas(existentes) {
  const ativas = (existentes || []).filter(r => r && r.flatId && r.checkIn && r.nomeHospede && r.status !== 'cancelada');
  const grupos = new Map();
  for (const r of ativas) {
    const k = r.flatId + '|' + r.checkIn + '|' + normalizarNome(r.nomeHospede);
    if (!grupos.has(k)) grupos.set(k, []);
    grupos.get(k).push(r);
  }
  const auto = [], ambi = [], zeroOrfas = [];
  const v = x => parseFloat(x.valorTotal) || 0;
  for (const g of grupos.values()) {
    if (g.length === 1) {
      const r = g[0], obs = String(r.observacoes || '');
      if (v(r) === 0 && /Importado via/i.test(obs) && !/Cortesia confirmada/i.test(obs)) zeroOrfas.push(r);
      continue;
    }
    const comValor = g.filter(x => v(x) > 0), semValor = g.filter(x => v(x) === 0);
    if (comValor.length === 1 && semValor.length >= 1) {
      const dropaveis = semValor.filter(x => !x.comCheckin);
      dropaveis.forEach(x => auto.push({ keep: comValor[0], drop: x, motivo: 'R$0 com par' }));
      if (dropaveis.length < semValor.length) ambi.push({ reservas: g, motivo: 'com check-in feito' });
      continue;
    }
    if (comValor.length === 0 && semValor.length >= 2) { ambi.push({ reservas: g, motivo: 'todas R$0' }); continue; }
    if (comValor.length >= 2) {
      const distintos = new Set(comValor.map(x => (Math.round(v(x) * 100) / 100).toFixed(2)));
      if (distintos.size === 1 && semValor.length === 0 && !comValor.some(x => x.comCheckin)) {
        const ordenado = [...comValor].sort(antigaPrimeiro);
        for (let i = 1; i < ordenado.length; i++) auto.push({ keep: ordenado[0], drop: ordenado[i], motivo: 'idênticas' });
        continue;
      }
      // (3510) o MdM chama de 'valores diferentes' tudo que não é o caso idêntico
      ambi.push({ reservas: g, motivo: distintos.size === 1 && semValor.length === 0 ? 'idênticas, uma com check-in' : 'valores diferentes' });
      continue;
    }
    ambi.push({ reservas: g, motivo: 'misto' });
  }
  return { auto, ambi, zeroOrfas };
}
// `ldup_ambiMarcarTodos` (3890): qual reserva do grupo fica, em cada modo;
// null = nenhuma escolha (modos keep-all e skip são da tela).
export const MODOS_EM_LOTE = Object.freeze([
  { chave: 'maior', rotulo: 'Manter maior valor' }, { chave: 'menor', rotulo: 'Manter menor valor' },
  { chave: 'recente', rotulo: 'Manter mais recente' }, { chave: 'antiga', rotulo: 'Manter mais antiga' },
  { chave: 'keep-all', rotulo: 'São diferentes — manter todas' }, { chave: 'skip', rotulo: 'Limpar marcações' },
]);
export function escolherEmLote(grupo, modo) {
  const rs = [...(grupo.reservas || [])];
  const v = x => parseFloat(x.valorTotal) || 0;
  if (modo === 'maior') rs.sort((a, b) => v(b) - v(a));
  else if (modo === 'menor') rs.sort((a, b) => { if (v(a) === 0 && v(b) > 0) return 1; if (v(b) === 0 && v(a) > 0) return -1; return v(a) - v(b); });
  else if (modo === 'recente') rs.sort((a, b) => antigaPrimeiro(b, a));
  else if (modo === 'antiga') rs.sort(antigaPrimeiro);
  else return null;
  return rs[0] || null;
}
// o resumo do topo da modal (3653) e a mensagem final (3869)
export const CARIMBO_CORTESIA = 'Cortesia confirmada';
export function resumoDaLimpeza({ apagadas = 0, atualizadas = 0, erros = 0 }) {
  let msg = `Limpeza concluída: ${apagadas} reserva(s) apagada(s), ${atualizadas} atualizada(s)`;
  if (erros) msg += `, ${erros} com erro`;
  return msg;
}

// ── o arquivo flat a flat (3139) ─────────────────────────────────────
// rows já reconfiguradas. Devolve o que a tela mostra no preview e o que
// `planejar` consome.
export function processarLinhas(rows, plataforma) {
  const header = (rows[0] || []).map(c => String(c || ''));
  const cols = detectarColunas(header);
  const amostras = [];
  for (let k = 1; k < rows.length; k++) {
    const r = rows[k] || [];
    if (cols.checkIn !== undefined && r[cols.checkIn]) amostras.push(r[cols.checkIn]);
    if (cols.checkOut !== undefined && r[cols.checkOut]) amostras.push(r[cols.checkOut]);
  }
  const fmtData = detectarFormatoData(amostras, plataforma || 'airbnb');
  const dados = []; let checkoutCalculado = false;
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every(c => !c)) continue;
    const ci = cols.checkIn !== undefined ? parseData(row[cols.checkIn], fmtData) : '';
    let co = cols.checkOut !== undefined ? parseData(row[cols.checkOut], fmtData) : '';
    const noites = cols.noites !== undefined ? parseInt(String(row[cols.noites] || '').replace(/[^\d]/g, ''), 10) : 0;
    if (!co && ci && noites > 0) { co = somarDiasISO(ci, noites); checkoutCalculado = true; }
    const obj = {
      nomeHospede: cols.nomeHospede !== undefined ? String(row[cols.nomeHospede] || '').trim() : '',
      checkIn: ci, checkOut: co,
      valorTotal: cols.valorTotal !== undefined ? parseValor(row[cols.valorTotal]) : 0,
      telefone: cols.telefone !== undefined ? String(row[cols.telefone] || '').trim() : '',
      email: cols.email !== undefined ? String(row[cols.email] || '').trim() : '',
      status: 'confirmada',
    };
    if (obj.checkIn) dados.push(obj);
  }
  let naoMapeadas = ['nomeHospede', 'checkIn', 'checkOut'].filter(k => cols[k] === undefined);
  if (checkoutCalculado) naoMapeadas = naoMapeadas.filter(k => k !== 'checkOut');
  return { dados, cols, fmtData, checkoutCalculado, naoMapeadas, semColunaValor: cols.valorTotal === undefined && dados.length > 0 };
}

// O plano de gravação (3250–3274 / 4399–4423): para cada linha do arquivo,
// contra as existentes ATIVAS do mesmo flat, a ação. `editaFinanceiro`
// falso: sem valor não há o que atualizar — o arquivo com valor contra a
// existente "0" (que a tela não vê) é a mesma reserva, IGNORAR.
export function planejar(dados, existentes, flatId, { editaFinanceiro = true } = {}) {
  const plano = { inserir: [], atualizar: [], conflitos: [], ignoradas: 0 };
  for (const r of dados) {
    const rNorm = { flatId, checkIn: r.checkIn, nomeHospede: r.nomeHospede, valorTotal: r.valorTotal };
    const existente = (existentes || []).find(x => x.flatId === flatId && x.status !== 'cancelada' && ehMesma(rNorm, x));
    if (existente) {
      let acao = decidirAcao(rNorm, existente);
      if (!editaFinanceiro && (acao === 'ATUALIZAR_VALOR' || acao === 'CONFIRMAR')) acao = 'IGNORAR';
      if (acao === 'IGNORAR') { plano.ignoradas++; continue; }
      if (acao === 'ATUALIZAR_VALOR') { plano.atualizar.push({ existente, valor: parseFloat(r.valorTotal) || 0 }); continue; }
      if (acao === 'CONFIRMAR') { plano.conflitos.push({ novo: r, existente }); continue; }
    }
    plano.inserir.push({ ...r, flatId });
  }
  return plano;
}
// As sumidas (3310): mesma plataforma + mesmo flat + check-in no range do
// arquivo + não cancelada + não casou com nenhuma linha.
export function sumidas(existentes, dados, flatId, plataforma) {
  const ci = dados.map(r => r.checkIn).filter(Boolean).sort();
  if (!ci.length) return [];
  const min = ci[0], max = ci[ci.length - 1];
  return (existentes || []).filter(x => {
    if (x.flatId !== flatId || x.plataforma !== plataforma || x.status === 'cancelada') return false;
    // 0084: reserva com hóspede dentro não se cancela (o banco recusa); se
    // ela "sumiu" do arquivo com alguém no quarto, quem resolve é o
    // check-out, não a importação
    if (x.comHospedeDentro) return false;
    if (!x.checkIn || x.checkIn < min || x.checkIn > max) return false;
    return !dados.some(r => ehMesma({ checkIn: r.checkIn, nomeHospede: r.nomeHospede }, x));
  });
}
// o motivo do cancelamento de uma sumida (divergência: cancela, não apaga)
export const motivoSumida = plataforma => `Sumiu do relatório ${capitalizar(plataforma)} importado`;
// a mensagem do fim (3327)
export function resumoDaImportacao({ novas = 0, atualizadas = 0, duplicadas = 0, conflitos = 0, erros = 0, flats = null }) {
  let msg = flats == null ? `${novas} nova(s) reserva(s) importada(s)` : `${novas} reserva(s) importada(s) em ${flats} flat(s)`;
  if (atualizadas) msg += `, ${atualizadas} atualizada(s) (valor preenchido)`;
  if (duplicadas) msg += `, ${duplicadas} duplicada(s) ignorada(s)`;
  if (conflitos) msg += `, ${conflitos} conflito(s) de valor (vou perguntar a seguir)`;
  if (erros) msg += `, ${erros} com erro`;
  return msg;
}

// ── o Relatório Completo (4175–4288) ─────────────────────────────────
export function colunaAnuncio(header) {
  let col = -1;
  (header || []).forEach((c, idx) => { const n = norm(c); if (n === 'anuncio' || n === 'listing' || n === 'titulo' || n === 'titulo do anuncio' || n === 'property') col = idx; });
  return col;
}
// Booking (4175): A=propriedade (nº do flat no fim), C=hóspede, E=chegada,
// F=partida, I=pagamento, J=comissão. `unidades` = [{id, numero}].
export function processarBooking(rows, unidades) {
  const linhas = [], semMapeamento = {};
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every(c => c === null || c === undefined || c === '')) continue;
    const nomeProp = String(row[0] || '').trim();
    if (!nomeProp) continue;
    const numeroFlat = extrairNumeroFlat(nomeProp);
    const checkIn = parseDataPtBrLonga(row[4]) || parseData(row[4], 'auto');
    const checkOut = parseDataPtBrLonga(row[5]) || parseData(row[5], 'auto');
    if (!checkIn || !checkOut) continue;
    const pagto = parseValor(row[8]), comissao = parseValor(row[9]);
    const obj = { anuncio: nomeProp, numeroFlat, nomeHospede: String(row[2] || '').trim(), checkIn, checkOut,
      valorTotal: Math.max(0, pagto - comissao), valorBruto: pagto, comissaoPlataforma: comissao, telefone: '', email: '', status: 'confirmada', plataforma: 'booking' };
    const flat = (unidades || []).find(f => String(f.numero) === numeroFlat);
    if (flat) { obj.flatId = flat.id; linhas.push(obj); }
    else { (semMapeamento[nomeProp] = semMapeamento[nomeProp] || []).push(obj); }
  }
  return { linhas, semMapeamento, formato: 'pt-BR longa', plataforma: 'booking', checkoutCalculado: false, semColunaValor: false };
}
// Airbnb por anúncio (4219). `anuncios` = [{titulo, unidade_id}].
// Devolve { erro, header } quando não há coluna "Anúncio".
export function processarRelatorio(rows, plataforma, anuncios, unidades) {
  if (rows[0] && rows[0][0]) rows[0][0] = String(rows[0][0]).replace(/^﻿/, '');
  if (plataforma === 'booking') return processarBooking(rows, unidades);
  const header = (rows[0] || []).map(c => String(c || ''));
  const cols = detectarColunas(header);
  const colAnuncio = colunaAnuncio(header);
  if (colAnuncio < 0) return { erro: 'sem-anuncio', header };
  const amostras = [];
  for (let k = 1; k < rows.length; k++) {
    const r = rows[k] || [];
    if (cols.checkIn !== undefined && r[cols.checkIn]) amostras.push(r[cols.checkIn]);
    if (cols.checkOut !== undefined && r[cols.checkOut]) amostras.push(r[cols.checkOut]);
  }
  const fmtData = detectarFormatoData(amostras, plataforma);
  const linhas = [], semMapeamento = {}; let checkoutCalculado = false;
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every(c => !c)) continue;
    const anuncio = String(row[colAnuncio] || '').trim();
    if (!anuncio) continue;
    const ci = cols.checkIn !== undefined ? parseData(row[cols.checkIn], fmtData) : '';
    let co = cols.checkOut !== undefined ? parseData(row[cols.checkOut], fmtData) : '';
    const noites = cols.noites !== undefined ? parseInt(String(row[cols.noites] || '').replace(/[^\d]/g, ''), 10) : 0;
    if (!co && ci && noites > 0) { co = somarDiasISO(ci, noites); checkoutCalculado = true; }
    const obj = { anuncio, nomeHospede: cols.nomeHospede !== undefined ? String(row[cols.nomeHospede] || '').trim() : '', checkIn: ci, checkOut: co,
      valorTotal: cols.valorTotal !== undefined ? parseValor(row[cols.valorTotal]) : 0,
      telefone: cols.telefone !== undefined ? String(row[cols.telefone] || '').trim() : '', email: cols.email !== undefined ? String(row[cols.email] || '').trim() : '', status: 'confirmada' };
    if (!obj.checkIn) continue;
    const map = (anuncios || []).find(m => m.titulo === anuncio);   // ipsis litteris
    if (map) { obj.flatId = map.unidade_id; linhas.push(obj); }
    else { (semMapeamento[anuncio] = semMapeamento[anuncio] || []).push(obj); }
  }
  return { linhas, semMapeamento, formato: fmtData, plataforma, checkoutCalculado, semColunaValor: cols.valorTotal === undefined };
}
// o preview por flat (4296), em ordem numérica
export function porFlat(linhas, unidades) {
  const grupos = new Map();
  for (const r of linhas || []) { if (!grupos.has(r.flatId)) grupos.set(r.flatId, []); grupos.get(r.flatId).push(r); }
  const numero = id => { const u = (unidades || []).find(x => x.id === id); return u ? parseInt(u.numero, 10) || 0 : 0; };
  return [...grupos.entries()].sort((a, b) => numero(a[0]) - numero(b[0]))
    .map(([flatId, itens]) => ({ flatId, itens, total: itens.reduce((s, r) => s + (r.valorTotal || 0), 0) }));
}
export const totalSemMapeamento = semMap => Object.keys(semMap || {}).reduce((s, k) => s + semMap[k].length, 0);
// os anúncios em ordem de flat (3972)
export function anunciosOrdenados(anuncios, unidades) {
  const numero = id => { const u = (unidades || []).find(x => x.id === id); return u ? parseInt(u.numero, 10) || 0 : 0; };
  return [...(anuncios || [])].sort((a, b) => numero(a.unidade_id) - numero(b.unidade_id));
}

// ── desfazer (4503) ──────────────────────────────────────────────────
// o lote mais recente entre as reservas importadas ainda existentes
export function ultimoLote(reservas) {
  const com = (reservas || []).filter(r => r.lote_importacao);
  if (!com.length) return null;
  com.sort((a, b) => String(b.criado_em || '').localeCompare(String(a.criado_em || '')));
  return com[0].lote_importacao;
}
export function resumoDoLote(reservas, lote, { valorDe, hospedeDe } = {}) {
  const itens = (reservas || []).filter(r => r.lote_importacao === lote);
  const flats = new Set(itens.map(r => r.unidade_id)), plats = new Set(itens.map(r => r.plataforma || 'outro'));
  const valor = valorDe ? itens.reduce((s, r) => s + (Number(valorDe(r)) || 0), 0) : null;
  const semNome = hospedeDe ? itens.filter(r => !hospedeDe(r) || hospedeDe(r) === NOME_SEM_HOSPEDE).length : 0;
  const quando = itens.map(r => r.criado_em).filter(Boolean).sort().pop() || null;
  return { reservas: itens.length, flats: flats.size, plataformas: [...plats].sort(), valor, semNome, quando, comCheckin: itens.filter(r => (r.hospedes || []).some(h => h.chegada_em)).length };
}
